/* ============================================================
   CADtomie — Supabase client (shared)
   ------------------------------------------------------------
   Single Supabase client used by every CADtomie app. Differs
   from a vanilla `createClient(url, anon)` in three ways:

     1. Storage = cookie on `.cadtomie.com` (cross-subdomain
        session sharing). Falls back to localStorage in dev.
     2. PKCE flow — modern SPA-safe code exchange.
     3. One-time migration from the default Supabase localStorage
        key (`sb-<projectRef>-auth-token`) into our cookie under
        a stable key (`cad-auth`). This lets existing planner
        users keep their session after the rollout — no forced
        re-login.

   Long-session behaviour ("remember-me") comes for free:
   - Cookie Max-Age = 30 days (rolling, see cookieStorage.ts)
   - Supabase `autoRefreshToken: true` keeps the access token
     fresh as long as the app is opened within the refresh-token
     lifetime configured in the Supabase dashboard.
   - No idle re-auth on our side. The OR-friendly default.
   ============================================================ */

import { createClient } from "@supabase/supabase-js";
import { createCookieStorage } from "./cookieStorage";

// Read env at module load. Both apps already pass these as
// VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY (Planner does this today;
// Intraop will adopt the same names when it wires auth in).
const url = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim();
const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim();

if (!url || !anonKey) {
  throw new Error(
    "VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set in the app's .env file",
  );
}

/** Stable storage key used by every CADtomie surface. Independent
 *  from the Supabase project ref so a backend swap doesn't bump
 *  every active user out of their session. */
const STORAGE_KEY = "cad-auth";

const storage = createCookieStorage();

// ── One-time migration from legacy localStorage ─────────────
// Existing planner users have a session under `sb-<ref>-auth-token`
// in localStorage. On first load after rollout we copy that into
// our cookie storage and clean up. Wrapped in try/catch because
// some browsers (Safari private, embedded WebViews) throw on
// localStorage access.
if (typeof window !== "undefined") {
  try {
    const ls = window.localStorage;
    if (ls && !storage.getItem(STORAGE_KEY)) {
      for (let i = 0; i < ls.length; i++) {
        const key = ls.key(i);
        if (key && /^sb-[a-z0-9]+-auth-token$/i.test(key)) {
          const value = ls.getItem(key);
          if (value) {
            storage.setItem(STORAGE_KEY, value);
            ls.removeItem(key);
          }
          break;
        }
      }
    }
  } catch {
    // Storage inaccessible (private mode, ITP eviction, etc.) — skip.
  }
}

export const supabase = createClient(url, anonKey, {
  auth: {
    storage,
    storageKey: STORAGE_KEY,
    flowType: "pkce",
    persistSession: true,
    autoRefreshToken: true,
    // Picks up the session from the URL fragment after OAuth /
    // email-link redirects — required for password reset and
    // any future OAuth providers.
    detectSessionInUrl: true,
  },
});

/** Convenience: returns the current Bearer token, or null if
 *  the user is not signed in. */
export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
